import React, { useState } from 'react';
import styled from 'styled-components';

import Card from '@components/Card';
import Select from '@components/Select';
import useAPI from '@hooks/useAPI';
import Button from '@shared/Button';
import Modal from '@shared/Modal';
import Spinner from '@shared/Spinner';

import {
  getAccessibleEnvironments,
  getCurrentBackendEnv,
  getEnvLabel,
  ModalContainer,
  SummarySection,
  ErrorItem,
  ErrorRuleName,
  ErrorDetail,
  DirectionLabel,
  CloseButtonRow,
  DiffSummary,
  ValidationErrors,
  RawJsonView,
  getValidationErrors,
} from './RuleDiffShared';

const Container = styled.div`
  width: 70%;
  margin: auto;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const SelectContainer = styled.div`
  width: 250px;
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0 0 16px 0;
`;

const CurrentEnvLabel = styled.span`
  font-size: 13px;
  color: #555;
`;

/* ── Modal ── */

function EnvDiffModal({ response, isLoading, error, handleClose }) {
  // Unified response shape across every rules endpoint: { status, details,
  // diff }. Errors arrive as per-rule objects under `details`; the diff (with
  // updated_rules / removed_rules) lives under `diff`, and /rules/diff adds
  // source / destination at the response root.
  const validationErrors = getValidationErrors(response);
  const hasValidationErrors = !!validationErrors;
  // Read the diff from the `diff` wrapper; the older flat-payload fallback
  // (diff fields at the root) is kept for resilience.
  const diff =
    response && !hasValidationErrors ? response.diff || response : null;
  const hasDiff =
    !!diff &&
    (diff.updated_rules !== undefined || diff.removed_rules !== undefined);
  const showRequestError = error && !hasValidationErrors && !hasDiff;
  const source = diff?.source || response?.source || '';
  const destination = diff?.destination || response?.destination || '';

  return (
    <Modal
      id='env-diff-modal'
      open
      toggle={handleClose}
      closeOnClickOutside
      maxWidth={900}
    >
      <Card title='Environment Rule Diff' shadow>
        <ModalContainer>
          {isLoading && <Spinner center />}

          {showRequestError && (
            <SummarySection>
              <ErrorItem>
                <ErrorRuleName>Request Error</ErrorRuleName>
                <ErrorDetail>{error}</ErrorDetail>
              </ErrorItem>
            </SummarySection>
          )}

          {hasValidationErrors && (
            <ValidationErrors errors={validationErrors} />
          )}

          {hasDiff && (
            <>
              <DirectionLabel>
                Comparing rules from <strong>{source}</strong> against{' '}
                <strong>{destination}</strong>
              </DirectionLabel>
              <DiffSummary
                diff={diff}
                emptyMessage='No rule differences found between the two environments.'
              />
              <RawJsonView src={diff} />
            </>
          )}

          <CloseButtonRow>
            <Button onClick={handleClose}>Close</Button>
          </CloseButtonRow>
        </ModalContainer>
      </Card>
    </Modal>
  );
}

/* ── Main component ── */

export default function EnvironmentRuleDiff() {
  const currentEnv = getCurrentBackendEnv();
  const currentLabel = getEnvLabel(currentEnv);

  const sourceOptions = getAccessibleEnvironments().filter(
    ([key]) => key !== currentEnv
  );

  const [selectedSource, setSelectedSource] = useState('');
  const [diffParams, setDiffParams] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const diffResponse = useAPI(...diffParams);

  const handleCompare = () => {
    if (!selectedSource) return;
    setShowModal(true);
    setDiffParams([
      'post',
      'ScreenConsoleApi',
      `config/${process.env.REACT_APP_CUSTOMER_ID}/rules/diff`,
      Date.now().toString(),
      false,
      '',
      false,
      { source: selectedSource },
    ]);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    // Clear the request params so useAPI resets its state (response, error,
    // errorData). Without this, a previous 4xx/5xx error would linger and
    // show the moment the modal is reopened.
    setDiffParams([]);
  };

  return (
    <Container>
      <HelpText>
        Compare rules from another environment against this environment
        to preview what would differ.
      </HelpText>

      <Row>
        <SelectContainer>
          <Select
            id='compare-env-source-select'
            options={sourceOptions}
            value={selectedSource}
            onChange={e => setSelectedSource(e.target.value)}
            placeholder='Select source environment'
            style={{ height: '43px' }}
          />
        </SelectContainer>
        <CurrentEnvLabel>
          → Current environment: <strong>{currentLabel}</strong>
        </CurrentEnvLabel>
      </Row>

      <Row>
        <Button onClick={handleCompare} disabled={!selectedSource}>
          Compare
        </Button>
      </Row>

      {showModal && (
        <EnvDiffModal
          response={diffResponse.response || diffResponse.errorData}
          isLoading={diffResponse.isLoading}
          error={diffResponse.error}
          handleClose={handleCloseModal}
        />
      )}
    </Container>
  );
}
